import * as React from 'react';
import { CheckIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';

import { Badge, Button, Dropdown, DropdownItem, IconButton } from '@restworld/ui-ds-base';
import { useTranslation } from 'next-i18next';

import { cn } from '@restworld/utils-common';
interface ReferenceCardProps {
  variant: 'new_received' | 'visible' | 'hidden';
  employerName: string;
  venueName: string;
  referenceText: string;
  actions?: {
    onHideReference?: () => void;
    onShowReference?: () => void;
    loading?: boolean;
  };
  madeVisibleToEmployerAt?: string;
  workingRelationship?: string;
}

const CardContainer = ({ variant, className, ...props }: any) => (
  <div
    className={cn(
      'min-w-[320px] max-w-2xl w-full p-4 pt-2 bg-neutral-10 rounded-2xl border border-neutral-90 inline-flex flex-col justify-start items-center gap-6',
      variant === 'new_received' && 'pt-4',
      className
    )}
    {...props}
  />
);

const CardContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch flex flex-col justify-start items-start gap-4', className)} {...props} />
);

const CardHeader = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('self-stretch justify-start', className)} {...props} />
);

const Highlight = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-700 text-base font-bold', className)} {...props} />
);

const Normal = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-700 text-base font-normal', className)} {...props} />
);

const ReferenceBox = ({ bgColor, className, ...props }: any) => (
  <div
    className={cn(
      'self-stretch p-4 rounded-lg inline-flex justify-center items-center gap-2.5',
      bgColor === 'purple-50' ? 'bg-purple-50' : 'bg-neutral-30',
      className
    )}
    {...props}
  />
);

const ReferenceText = ({ textColor, className, ...props }: any) => (
  <h5
    className={cn(
      'flex-1 justify-start text-neutral-700 text-base font-normal',
      textColor === 'neutral-200' ? 'text-neutral-200' : 'text-neutral-700',
      className
    )}
    {...props}
  />
);

const Divider = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <hr className={cn('self-stretch h-px relative bg-neutral-40 rounded-full', className)} {...props} />
);

const Actions = ({ loading, className, ...props }: any) => (
  <div
    className={cn(
      'self-stretch inline-flex justify-start items-center gap-4 relative',
      loading && 'pointer-events-none',
      className
    )}
    {...props}
  />
);

const DateText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h5 className={cn('self-start text-neutral-200 text-sm font-normal', className)} {...props} />
);

type CardType = ReferenceCardProps & {
  badge: React.ReactNode;
  dropdownItems?: DropdownItem[];
  actions?: React.ReactNode;
  showDate?: boolean;
  madeVisibleToEmployerAt?: string;
};

const ReferenceCardBase: React.FC<CardType> = ({
  employerName,
  venueName,
  referenceText,
  badge,
  dropdownItems,
  actions,
  showDate,
  madeVisibleToEmployerAt,
  variant,
  workingRelationship,
}) => {
  const { t } = useTranslation('worker-references-page');
  return (
    <CardContainer variant={variant}>
      <CardContent>
        <div className="self-stretch inline-flex justify-between items-center">
          {badge}
          {dropdownItems && (
            <Dropdown
              triggerElement={<IconButton Icon={EllipsisHorizontalIcon} ariaLabel="options" />}
              items={dropdownItems}
            />
          )}
        </div>
        <CardHeader>
          <Highlight>{employerName}</Highlight>
          <Normal>, {t('reference_card.title.your_former_employee')} </Normal>
          <Highlight>{workingRelationship}</Highlight>
          <Normal> {t('reference_card.title.at')} </Normal>
          <Highlight>{venueName}</Highlight>
          <Normal>, {t('reference_card.title.wrote_you_this_reference')}:</Normal>
        </CardHeader>
        <ReferenceBox bgColor={variant === 'visible' ? 'purple-50' : 'neutral-30'}>
          <ReferenceText textColor={variant === 'hidden' ? 'neutral-200' : 'neutral-700'}>
            {referenceText}
          </ReferenceText>
        </ReferenceBox>
      </CardContent>
      {actions}
      {showDate && <DateText>{madeVisibleToEmployerAt}</DateText>}
    </CardContainer>
  );
};

export const ReferenceCard: React.FC<ReferenceCardProps> = ({ variant, actions, ...rest }) => {
  const { t } = useTranslation('worker-references-page');
  const [action, setAction] = React.useState<'accept' | 'reject'>();
  switch (variant) {
    case 'new_received':
      return (
        <ReferenceCardBase
          {...rest}
          badge={
            <Badge variant="fresh">
              <span role="img" aria-label="badge">
                {t('reference_card.new_reference')}
              </span>
            </Badge>
          }
          actions={
            <>
              <Divider />
              <Actions>
                <Button
                  fullwidth
                  variant="tertiary"
                  className="shadow-none"
                  StartIcon={EyeSlashIcon}
                  onClick={() => {
                    setAction('reject');
                    actions?.onHideReference?.();
                  }}
                  loading={action === 'reject' && actions?.loading}
                >
                  {t('reference_card.actions.secondary')}
                </Button>
                <Button
                  fullwidth
                  variant="primary"
                  StartIcon={CheckIcon}
                  onClick={() => {
                    setAction('accept');
                    actions?.onShowReference?.();
                  }}
                  loading={action === 'accept' && actions?.loading}
                >
                  {t('reference_card.actions.primary')}
                </Button>
              </Actions>
            </>
          }
          variant={variant}
        />
      );
    case 'visible':
      return (
        <ReferenceCardBase
          {...rest}
          badge={
            <Badge variant="restworld" icon={EyeIcon}>
              {t('reference_card.visible_to_employers')}
            </Badge>
          }
          dropdownItems={[
            {
              label: t('reference_card.actions.hide_reference'),
              Icon: EyeSlashIcon,
              onClick: () => {
                actions?.onHideReference?.();
              },
            },
          ]}
          showDate
          variant={variant}
        />
      );
    case 'hidden':
    default:
      return (
        <ReferenceCardBase
          {...rest}
          badge={
            <Badge variant="disabled" icon={EyeSlashIcon}>
              {t('reference_card.only_visible_to_you')}
            </Badge>
          }
          dropdownItems={[
            {
              label: t('reference_card.actions.show_reference'),
              Icon: EyeIcon,
              onClick: () => {
                actions?.onShowReference?.();
              },
            },
          ]}
          showDate
          variant={variant}
        />
      );
  }
};

export default ReferenceCard;
